/**
 * 发布项目到 App 弹窗
 * 
 * v11.2.2 更新：支持角色展示信息（头像、角色名称、副标题、介绍）
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Loader2, Search, Crown, CheckCircle2, Upload, ImagePlus, X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useToast } from '@/shared/hooks/useToast'
import { ProjectApiService } from '@/modules/agent/services/project-api'
import { ProjectPublishApiService, uploadAvatarToS3 } from '../services/app-api'
import type { Category, PublishProjectRequest } from '../types/app'
import type { AgentProject } from '@/modules/agent/types/project'

interface ProjectPublishDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  appId: string
  categories: Category[]
  onSuccess: () => void
}

export default function ProjectPublishDialog({
  open,
  onOpenChange,
  appId,
  categories,
  onSuccess,
}: ProjectPublishDialogProps) {
  const { showSuccess, showError } = useToast()

  // 步骤状态
  const [step, setStep] = useState<'select' | 'config'>('select')

  // 项目选择状态
  const [projects, setProjects] = useState<AgentProject[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProject, setSelectedProject] = useState<AgentProject | null>(null)

  // 发布配置状态
  const [config, setConfig] = useState({
    // v11.2.2 新增 - 角色展示信息
    avatar_url: '',
    display_name: '',
    subtitle: '',
    description: '',
    // 原有字段
    subscription_tier: 'free' as 'free' | 'tier1' | 'tier2',
    category: '__none__',
    is_recommended: false,
    recommend_sort_order: 0,
    tags: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // v11.2.2: 头像上传状态
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 加载项目列表
  const loadProjects = useCallback(async () => {
    setIsLoadingProjects(true)
    try {
      const response = await ProjectApiService.getProjects({})
      console.log('📦 [ProjectPublishDialog] API 响应:', response)
      if (response.success && response.data) {
        // response.data 是 AgentProject[]（数组），不是 { projects: [...] }
        const projectList = Array.isArray(response.data) ? response.data : []
        console.log('📦 [ProjectPublishDialog] 原始项目数:', projectList.length)
        console.log('📦 [ProjectPublishDialog] 项目发布状态:', 
          projectList.map(p => ({ 
            name: p.name, 
            published_version: p.published_version,
            published_at: p.published_at 
          }))
        )
        // 过滤出已发布 Flow 的项目（有 published_version 表示已发布）
        const publishedProjects = projectList.filter((p: AgentProject) => 
          p.published_version !== undefined && p.published_version !== null
        )
        console.log('📦 [ProjectPublishDialog] 过滤后项目数:', publishedProjects.length)
        setProjects(publishedProjects)
      }
    } catch (error) {
      console.error('❌ [ProjectPublishDialog] 加载项目列表失败:', error)
      showError('加载项目列表失败')
    } finally {
      setIsLoadingProjects(false)
    }
  }, [showError])

  useEffect(() => {
    if (open) {
      loadProjects()
      // 重置状态
      setStep('select')
      setSelectedProject(null)
      setSearchQuery('')
      setConfig({
        // v11.2.2 新增
        avatar_url: '',
        display_name: '',
        subtitle: '',
        description: '',
        // 原有字段
        subscription_tier: 'free',
        category: '__none__',
        is_recommended: false,
        recommend_sort_order: 0,
        tags: '',
      })
      // 重置头像状态
      setAvatarFile(null)
      setAvatarPreview(null)
    }
  }, [open, loadProjects])

  // 过滤项目
  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // 选择项目进入配置
  const handleSelectProject = (project: AgentProject) => {
    setSelectedProject(project)
    // v11.2.2: 用项目信息初始化角色展示字段
    setConfig(prev => ({
      ...prev,
      display_name: project.name,
      description: project.description || '',
    }))
    setStep('config')
  }
  
  // v11.2.2: 处理头像文件选择
  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // 验证文件类型
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!validTypes.includes(file.type)) {
      showError('请选择 PNG、JPG 或 WebP 格式的图片')
      return
    }
    
    // 验证文件大小（最大 5MB）
    if (file.size > 5 * 1024 * 1024) {
      showError('图片大小不能超过 5MB')
      return
    }
    
    setAvatarFile(file)
    // 创建预览
    const reader = new FileReader()
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }
  
  // v11.2.2: 上传头像
  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !selectedProject) return null
    
    setIsUploadingAvatar(true)
    try {
      // 1. 获取上传 URL
      const response = await ProjectPublishApiService.getAvatarUploadUrl({
        project_id: selectedProject.id,
        file_name: avatarFile.name,
        content_type: avatarFile.type,
      })
      
      if (!response.success || !response.data) {
        throw new Error('获取上传 URL 失败')
      }
      
      // 2. 上传到 S3
      await uploadAvatarToS3(response.data.upload_url, avatarFile)
      
      return response.data.avatar_url
    } catch (error) {
      console.error('❌ [ProjectPublishDialog] 头像上传失败:', error)
      throw error
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  // 提交发布
  const handleSubmit = async () => {
    if (!selectedProject) return
    
    // v11.2.2: 验证必填字段 - 头像
    if (!avatarFile && !config.avatar_url) {
      showError('请上传角色头像')
      return
    }

    setIsSubmitting(true)
    try {
      // v11.2.2: 先上传头像（如果有新选择的文件）
      let avatarUrl = config.avatar_url
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar()
        if (!uploadedUrl) {
          showError('头像上传失败')
          return
        }
        avatarUrl = uploadedUrl
      }
      
      const request: PublishProjectRequest = {
        app_id: appId,
        // v11.2.2 新增 - 角色展示信息
        avatar_url: avatarUrl,
        display_name: config.display_name || undefined,
        subtitle: config.subtitle || undefined,
        description: config.description || undefined,
        // 原有字段
        subscription_tier: config.subscription_tier,
        category: config.category === '__none__' ? undefined : config.category,
        is_recommended: config.is_recommended,
        recommend_sort_order: config.is_recommended ? config.recommend_sort_order : undefined,
        tags: config.tags
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t),
      }

      const response = await ProjectPublishApiService.publishProject(
        selectedProject.id,
        request
      )

      if (response.success) {
        showSuccess(`项目「${selectedProject.name}」已发布到 App`)
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ [ProjectPublishDialog] 发布失败:', error)
      if (error.response?.data?.error?.code === 'FLOW_NOT_PUBLISHED') {
        showError('项目 Flow 未发布，请先发布 Flow')
      } else if (error.response?.data?.error?.code === 'ALREADY_PUBLISHED') {
        showError('项目已发布到此 App')
      } else if (error.response?.data?.error?.code === 'AVATAR_URL_REQUIRED') {
        showError('请上传角色头像')
      } else {
        showError('发布失败，请稍后重试')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {step === 'select' ? '选择要发布的项目' : '配置发布选项'}
          </DialogTitle>
          <DialogDescription>
            {step === 'select'
              ? '只有已发布 Flow 的项目才能发布到 App'
              : `正在发布：${selectedProject?.name}`}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' ? (
          <>
            {/* 搜索框 */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="搜索项目名称..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* 项目列表 */}
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {isLoadingProjects ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  {searchQuery
                    ? '没有找到匹配的项目'
                    : '没有可发布的项目（需要先发布 Flow）'}
                </div>
              ) : (
                filteredProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleSelectProject(project)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{project.name}</span>
                        {project.published_version && (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Flow 已发布
                          </Badge>
                        )}
                      </div>
                      {project.description && (
                        <p className="text-sm text-slate-500 truncate mt-1">
                          {project.description}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* v11.2.2: 角色展示信息区域 */}
            <div className="border rounded-lg p-4 bg-slate-50 space-y-4">
              <h4 className="font-medium text-sm text-slate-700">角色展示信息</h4>
              
              {/* 头像上传 */}
              <div className="grid gap-2">
                <Label>
                  角色头像 <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-start gap-4">
                  {/* 预览区域 */}
                  <div 
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-white cursor-pointer hover:border-primary transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {avatarPreview ? (
                      <img 
                        src={avatarPreview} 
                        alt="头像预览" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImagePlus className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleAvatarSelect}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                    >
                      {isUploadingAvatar ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      选择图片
                    </Button>
                    {avatarFile && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="truncate max-w-[150px]">{avatarFile.name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setAvatarFile(null)
                            setAvatarPreview(null)
                          }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500">
                      支持 PNG、JPG、WebP，建议 200x200，最大 5MB
                    </p>
                  </div>
                </div>
              </div>
              
              {/* 角色名称 */}
              <div className="grid gap-2">
                <Label>角色名称</Label>
                <Input
                  placeholder="如：Elena"
                  value={config.display_name}
                  onChange={(e) => setConfig({ ...config, display_name: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  留空则使用项目名称
                </p>
              </div>
              
              {/* 副标题 */}
              <div className="grid gap-2">
                <Label>角色定位</Label>
                <Input
                  placeholder="如：你的灵魂画师"
                  value={config.subtitle}
                  onChange={(e) => setConfig({ ...config, subtitle: e.target.value })}
                />
              </div>
              
              {/* 角色介绍 */}
              <div className="grid gap-2">
                <Label>角色介绍</Label>
                <Textarea
                  placeholder="在这里，色彩比语言更能表达情绪。让我们一起描绘你的内心世界。"
                  value={config.description}
                  onChange={(e) => setConfig({ ...config, description: e.target.value })}
                  rows={3}
                />
                <p className="text-xs text-slate-500">
                  独立于项目描述，用于 App 展示
                </p>
              </div>
            </div>
            
            {/* 订阅等级 */}
            <div className="grid gap-2">
              <Label>订阅等级</Label>
              <Select
                value={config.subscription_tier}
                onValueChange={(value: 'free' | 'tier1' | 'tier2') =>
                  setConfig({ ...config, subscription_tier: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-slate-400" />
                      免费
                    </div>
                  </SelectItem>
                  <SelectItem value="tier1">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-blue-500" />
                      基础版
                    </div>
                  </SelectItem>
                  <SelectItem value="tier2">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      高级版
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                用户需要达到对应订阅等级才能使用此项目
              </p>
            </div>

            {/* 分类 */}
            <div className="grid gap-2">
              <Label>分类</Label>
              <Select
                value={config.category}
                onValueChange={(value) => setConfig({ ...config, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择分类" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">不设置分类</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.category_id} value={category.category_id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 推荐设置 */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>设为推荐</Label>
                <p className="text-xs text-slate-500">推荐项目会在首页展示</p>
              </div>
              <Switch
                checked={config.is_recommended}
                onCheckedChange={(checked) =>
                  setConfig({ ...config, is_recommended: checked })
                }
              />
            </div>

            {/* 推荐排序（仅推荐时显示） */}
            {config.is_recommended && (
              <div className="grid gap-2">
                <Label>推荐排序</Label>
                <Input
                  type="number"
                  min="0"
                  value={config.recommend_sort_order}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      recommend_sort_order: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-32"
                />
                <p className="text-xs text-slate-500">数字越小越靠前</p>
              </div>
            )}

            {/* 标签 */}
            <div className="grid gap-2">
              <Label>标签</Label>
              <Input
                placeholder="多个标签用逗号分隔，如：INFJ,艺术,治愈"
                value={config.tags}
                onChange={(e) => setConfig({ ...config, tags: e.target.value })}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'config' && (
            <Button
              variant="outline"
              onClick={() => setStep('select')}
              disabled={isSubmitting}
            >
              返回选择
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            取消
          </Button>
          {step === 'config' && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  发布中...
                </>
              ) : (
                '发布项目'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
